import { SpinnerLegacy } from "../components";
import { LANDING, ORDERS, PRODUCT_LIST, SIGNUP } from "../constants/routes";
import { useSupplierProductExistence } from "../hooks/api/v2/product/useSupplierProductExistence";
import { useFeatureFlagStore } from "../hooks/useFeatureFlags";
import { Navigate, useSearchParams } from "react-router";
import { useReadLocalStorage } from "usehooks-ts";
import type { AppRedirectState } from "./resolveAppRedirect";
import { resolveAppRedirect } from "./resolveAppRedirect";

export const AppFallbackRedirect = ({
  hasFirebaseUser,
  sellarUser,
  business,
  redirectToMarket,
  redirectToSellarCreditOnboarding,
  ignoreContinuationRedirects = false,
}: Pick<
  AppRedirectState,
  | "hasFirebaseUser"
  | "sellarUser"
  | "business"
  | "redirectToMarket"
  | "redirectToSellarCreditOnboarding"
  | "ignoreContinuationRedirects"
>) => {
  const [searchParams] = useSearchParams();
  const onboardingContinueUrl = useReadLocalStorage<string | null>(
    "onboardingContinueUrl",
  );

  const redirectUrl = resolveAppRedirect({
    hasFirebaseUser,
    sellarUser,
    business,
    continueUrl: searchParams.get("continue"),
    queryParameters: searchParams.get("queryParameters"),
    onboardingContinueUrl,
    redirectToMarket,
    redirectToSellarCreditOnboarding,
    ignoreContinuationRedirects,
  });

  const onboardingEnabled = useFeatureFlagStore((store) =>
    store.isActive("PRODUCT_CONTAINER_ONBOARDING"),
  );
  // Only change the default supplier destination; explicit continue links win.
  const checkProducts =
    onboardingEnabled &&
    business?.type === "supplier" &&
    redirectUrl === ORDERS &&
    (ignoreContinuationRedirects || !searchParams.get("continue")) &&
    (ignoreContinuationRedirects ||
      !onboardingContinueUrl ||
      [LANDING, SIGNUP].includes(onboardingContinueUrl));
  const productsQuery = useSupplierProductExistence(checkProducts);

  if (checkProducts && productsQuery.isPending) return <SpinnerLegacy />;

  const needsProductSetup =
    checkProducts &&
    productsQuery.isSuccess &&
    !productsQuery.data.pages.some((page) => page.length > 0);
  return (
    <Navigate to={needsProductSetup ? PRODUCT_LIST : redirectUrl} replace />
  );
};
